package com.islamicmedia.shared.platform

import com.islamicmedia.shared.data.api.createHttpClient
import com.islamicmedia.shared.data.api.serverBaseUrl
import com.islamicmedia.shared.data.auth.SupabaseAuthService
import com.islamicmedia.shared.data.storage.SettingsStorage
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.contentType
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.launch
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlin.random.Random

/**
 * Registration / paywall funnel tracking — "how far did this person get?"
 *
 * Separate from [Analytics] on purpose, because they answer different questions and, critically,
 * have different reach:
 *
 *  - [Analytics] → Firebase. Mobile only: `AnalyticsTracker` on jsMain is a no-op, so the entire
 *    fitrahfilter.com audience is invisible to it. It is also aggregate-only — GA4 cannot show
 *    "this user searched, then registered, then dismissed the paywall".
 *  - [Funnel] → our own `funnel_events` table via POST /api/funnel. Works identically on Android,
 *    iOS and web, is readable in realtime from /admin, and keeps per-person ordering.
 *
 * Every [track] call goes to BOTH, so instrumenting a tap once feeds the admin funnel and Firebase
 * together and the two can't drift apart.
 *
 * ANONYMOUS BY DESIGN: the first funnel steps happen before the user exists, so the identity here
 * is [anonId] — a random id minted on first launch and persisted per device/browser. Once the user
 * signs in, their id/email ride along on subsequent events, which is what joins the two halves of
 * one journey. No PII is invented; the email only appears after they've given it.
 *
 * Fire-and-forget: a failed post is swallowed. Analytics must never break a user flow.
 */
object Funnel {

    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private val client by lazy { createHttpClient() }
    private val json = Json { ignoreUnknownKeys = true; encodeDefaults = true }
    private val storage by lazy { SettingsStorage() }

    /** Set false by the host app to disable reporting entirely (e.g. a debug switch). */
    var enabled: Boolean = true

    private var cachedAnonId: String? = null
    private var cachedAttribution: Attribution? = null
    private var attributionLoaded: Boolean = false

    /**
     * Stable per-device/browser id. Minted once and persisted; NOT reset on sign-out, because a
     * user who signs out and signs back in is the same journey and resetting would double-count
     * them in every funnel step.
     */
    val anonId: String
        get() = cachedAnonId ?: run {
            val existing = try { storage.loadAnonId() } catch (_: Exception) { null }
            val id = existing ?: randomId().also {
                try { storage.saveAnonId(it) } catch (_: Exception) { /* memory-only this run */ }
            }
            cachedAnonId = id
            id
        }

    /**
     * First-touch acquisition record for this device/browser, or null if nothing was ever captured
     * (every pre-feature install, and iOS forever — see [Attribution]).
     *
     * Read once and cached, including the null result: a person with no attribution would otherwise
     * hit storage on every single event.
     */
    val firstTouch: Attribution?
        get() {
            if (!attributionLoaded) {
                attributionLoaded = true
                cachedAttribution = try {
                    storage.loadAttribution()?.let { json.decodeFromString(Attribution.serializer(), it) }
                } catch (_: Exception) {
                    null
                }
            }
            return cachedAttribution
        }

    /**
     * Record where this person came from, if we do not already know.
     *
     * FIRST-TOUCH: an existing record is never overwritten. Callers fire this freely — the web
     * initializer on every page load, the Android install-referrer callback on every cold start —
     * and the guard here is what makes that safe, rather than each caller having to know.
     *
     * An empty record (no tag, no referrer, nothing) is NOT persisted, so a `(direct)` first visit
     * leaves capture open for a later visit that actually carries a source.
     */
    fun recordFirstTouch(attribution: Attribution) {
        if (attribution.isEmpty()) return
        if (firstTouch != null) return
        cachedAttribution = attribution
        attributionLoaded = true
        try {
            storage.saveAttribution(json.encodeToString(Attribution.serializer(), attribution))
        } catch (_: Exception) {
            // Memory-only for this run. Losing an analytics write must never surface to the user.
        }
    }

    /**
     * Record one funnel event.
     *
     * @param event  name from [Event] — snake_case, matching the Firebase event so both sides read
     *               the same vocabulary.
     * @param step   ordinal from [FunnelStep] for on-funnel events; null for branches/exits
     *               (dismiss, back) which are counted but don't advance the funnel.
     * @param params small flat extras (plan key, source). Values are stringified; keep them short.
     */
    fun track(event: String, step: Int? = null, params: Map<String, String> = emptyMap()) {
        // Firebase too — mobile-only, but free, and keeps the GA4 reports in step with ours.
        Analytics.logEvent(event, params)

        if (!enabled) return
        scope.launch {
            try {
                val user = try { SupabaseAuthService.instance.currentUser } catch (_: Exception) { null }
                val version = try {
                    "${com.islamicmedia.shared.util.appVersion} (${com.islamicmedia.shared.util.appBuildNumber})"
                } catch (_: Exception) { null }

                // Attached HERE rather than at the call sites, so every event carries acquisition
                // without 26 call sites each having to remember to pass it.
                val attr = firstTouch
                val body = FunnelEventRequest(
                    anonId = anonId,
                    userId = user?.id,
                    userEmail = user?.email,
                    event = event,
                    step = step,
                    appVersion = version,
                    params = params,
                    utmSource = attr?.source,
                    utmMedium = attr?.medium,
                    utmCampaign = attr?.campaign,
                    utmTerm = attr?.term,
                    utmContent = attr?.content,
                    referrerHost = attr?.referrerHost,
                    clickId = attr?.clickId,
                    clickIdType = attr?.clickIdType,
                    landingPath = attr?.landingPath,
                )
                client.post("${serverBaseUrl()}/api/funnel") {
                    contentType(ContentType.Application.Json)
                    version?.let { header("X-App-Version", it) }
                    setBody(json.encodeToString(FunnelEventRequest.serializer(), body))
                }
            } catch (_: Exception) {
                // Fire-and-forget. Losing an analytics row must never surface to the user.
            }
        }
    }

    /** 32 hex chars — collision-safe enough for an install id, and no UUID dependency in common code. */
    private fun randomId(): String =
        (0 until 32).joinToString("") { Random.nextInt(16).toString(16) }
}

@Serializable
internal data class FunnelEventRequest(
    val anonId: String,
    val userId: String? = null,
    val userEmail: String? = null,
    val event: String,
    val step: Int? = null,
    val appVersion: String? = null,
    val params: Map<String, String> = emptyMap(),
    // Acquisition (see Attribution). Sent as dedicated fields rather than inside `params`, which
    // the server drops WHOLESALE past 500 chars — smuggling utm values through it would take the
    // event's real params down with them.
    val utmSource: String? = null,
    val utmMedium: String? = null,
    val utmCampaign: String? = null,
    val utmTerm: String? = null,
    val utmContent: String? = null,
    val referrerHost: String? = null,
    val clickId: String? = null,
    val clickIdType: String? = null,
    val landingPath: String? = null,
)

/**
 * The registration funnel, in order. Ordinals are stored on each row so the admin can sort and
 * compute drop-off without hardcoding this list a second time in the dashboard.
 *
 * Steps are "furthest reached" markers, not a strict path — a returning user who is already signed
 * in enters at [PAYWALL_SHOWN] without passing through [SIGNED_UP], and the admin's step counts are
 * distinct-person counts per step rather than a claim that everyone walked every step.
 */
object FunnelStep {
    const val APP_OPENED = 1
    const val SEARCH = 2
    const val AUTH_SHOWN = 3
    const val SIGNED_UP = 4
    const val PAYWALL_SHOWN = 5
    const val PAYWALL_CONTINUE = 6
    const val PLAN_SHOWN = 7
    const val BEGIN_CHECKOUT = 8
    const val SUBSCRIBED = 9

    /** Ordered (ordinal, event, human label) — the single definition the admin funnel renders from. */
    val ALL: List<Triple<Int, String, String>> = listOf(
        Triple(APP_OPENED, Event.APP_OPENED, "Opened the app"),
        Triple(SEARCH, Event.SEARCH, "Searched a title"),
        Triple(AUTH_SHOWN, Event.AUTH_SHOWN, "Saw sign-up"),
        Triple(SIGNED_UP, Event.SIGN_UP, "Registered"),
        Triple(PAYWALL_SHOWN, Event.PAYWALL_SHOWN, "Saw the paywall"),
        Triple(PAYWALL_CONTINUE, Event.PAYWALL_CONTINUE, "Tapped \"Let's get started\""),
        Triple(PLAN_SHOWN, Event.PAYWALL_PLAN_SHOWN, "Saw the offer"),
        Triple(BEGIN_CHECKOUT, Event.BEGIN_CHECKOUT, "Started checkout"),
        Triple(SUBSCRIBED, Event.SUBSCRIBED, "Subscribed"),
    )

    /** Off-funnel exits, counted separately — these are how people LEAVE, not how they progress. */
    val EXITS: List<Pair<String, String>> = listOf(
        Event.PAYWALL_DISMISSED to "Closed the paywall (X)",
        Event.PAYWALL_BACK to "Went back from the offer",
        Event.AUTH_ABANDONED to "Left sign-up without registering",
    )
}
